import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { TranslatePipe } from '@core/i18n-legacy';
import type {
  SkillsImportProficiency,
  SkillsImportResultGroup,
  SkillsImportResultPosition,
  SkillsImportSkill,
} from '@core/tellent-skills-types';
import {
  ButtonComponent,
  FormFieldComponent,
  GroupComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
  RadioComponent,
  type Select2Item,
  SelectComponent,
  TruncateComponent,
} from '@core/user-interface-legacy';

import {
  ProficiencyEditFormComponent,
  type ProficiencyEditFormSaveEvent,
} from '../proficiency-edit-form/proficiency-edit-form.component';

@Component({
  selector: 'tlc-imported-skill-card',
  templateUrl: './imported-skill-card.component.html',
  styleUrl: './imported-skill-card.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    FormFieldComponent,
    GroupComponent,
    IconButtonComponent,
    IconComponent,
    InputComponent,
    LabelComponent,
    ProficiencyEditFormComponent,
    RadioComponent,
    ReactiveFormsModule,
    SelectComponent,
    TranslatePipe,
    TruncateComponent,
  ],
})
export class ImportedSkillCardComponent {
  protected readonly expanded = signal(true);
  protected readonly importDecision = signal<'create-or-update' | 'skip'>('create-or-update');
  protected readonly editMode = signal(false);
  protected readonly editingProficiencyIndex = signal<number | null>(null);

  public readonly skill = input.required<SkillsImportSkill>();
  public readonly groups = input.required<SkillsImportResultGroup[]>();
  public readonly positions = input.required<SkillsImportResultPosition[]>();
  public readonly skillSave = output<Pick<SkillsImportSkill, 'group' | 'name' | 'description'>>();
  public readonly proficiencySave = output<{
    index: number;
    data: Pick<SkillsImportProficiency, 'name' | 'description' | 'positions'>;
  }>();
  public readonly importDecisionChange = output<'create-or-update' | 'skip'>();
  public readonly collapsed = output<void>();

  public readonly groupItems = computed(() => [
    { option: '—', value: null },
    ...this.groups().map(group => ({ option: group.name, value: group.name })),
  ]);

  public readonly positionItems = computed<Array<Select2Item<string>>>(() =>
    this.positions().map(position => ({ label: position.name, value: position.name })),
  );

  public readonly form = new FormGroup({
    groupName: new FormControl<string | null>(null),
    name: new FormControl<string>('', { nonNullable: true }),
    description: new FormControl<string | null>(null),
  });

  protected toggleExpanded(): void {
    if (this.expanded()) {
      this.expanded.set(false);
      this.collapsed.emit();
    } else {
      this.expanded.set(true);
    }
  }

  protected setImportDecision(decision: 'create-or-update' | 'skip'): void {
    this.importDecision.set(decision);
    this.importDecisionChange.emit(decision);
  }

  protected proficiencyPositionNames(proficiency: SkillsImportProficiency): string {
    return proficiency.positions
      .map(position => position.positionName)
      .filter(name => name !== null)
      .join(', ');
  }

  protected enterEdit(): void {
    const skill = this.skill();
    this.editingProficiencyIndex.set(null);
    this.form.setValue({
      groupName: skill.group?.name ?? null,
      name: skill.name,
      description: skill.description,
    });
    this.editMode.set(true);
  }

  protected cancelEdit(): void {
    this.editMode.set(false);
  }

  protected save(): void {
    const { groupName, name, description } = this.form.getRawValue();
    const resolvedGroupName = groupName || null;
    const group =
      resolvedGroupName !== null
        ? (this.groups().find(group => group.name === resolvedGroupName) ?? {
            id: null,
            name: resolvedGroupName,
          })
        : null;
    this.skillSave.emit({ group, name, description });
    this.editMode.set(false);
  }

  protected enterProficiencyEdit(index: number): void {
    this.editMode.set(false);
    this.editingProficiencyIndex.set(index);
  }

  protected cancelProficiencyEdit(): void {
    this.editingProficiencyIndex.set(null);
  }

  protected saveProficiency(
    index: number,
    { name, description, positions: selectedNames }: ProficiencyEditFormSaveEvent,
  ): void {
    const currentPositions = this.skill().proficiencies[index].positions;
    const positions = selectedNames.map(positionName => {
      const existing = currentPositions.find(position => position.positionName === positionName);
      if (existing) return existing;
      const available = this.positions().find(position => position.name === positionName);
      return {
        existingPositionGuid: available?.existingPositionGuid ?? null,
        positionName,
        matchConfidence: null,
      };
    });
    this.proficiencySave.emit({ index, data: { name, description, positions } });
    this.editingProficiencyIndex.set(null);
  }
}
